import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import AppointmentServicesAPI from '../../api/appointmentServices';
import { extractRecord, extractList } from '../utils/appointmentEnvelope';
import {
  createAccountContextGuard,
  createAccountRequestGuard,
  invalidateAccountRequestContext,
} from './utils/accountRequestGuard';
import { createAbortableRequestTracker } from './utils/abortableRequestTracker';
import { createOperationRequestTracker } from './utils/operationRequestTracker';

const fetchRequestGuard = createAccountRequestGuard();
const mutationContextGuard = createAccountContextGuard();
const readRequestTracker = createAbortableRequestTracker();
const mutationRequestTracker = createOperationRequestTracker();

const normalizeMemberPayload = payload =>
  payload && typeof payload === 'object' ? payload : { id: payload };

const beginMutationRequest = (
  localState,
  rootGetters,
  { accountId, generation, group, identity }
) => {
  const contextRequest = mutationContextGuard.begin(localState, {
    accountId: accountId ?? rootGetters.getCurrentAccountId,
    generation,
  });
  const isCurrentContext = () =>
    mutationContextGuard.isCurrent(localState, contextRequest, rootGetters);

  if (!isCurrentContext()) {
    return {
      shouldStart: false,
      isCurrent: () => false,
      finish: () => false,
    };
  }

  const operationRequest = mutationRequestTracker.begin(localState, {
    group,
    identity,
  });

  return {
    accountId: contextRequest.accountId,
    shouldStart: true,
    isCurrent: () =>
      isCurrentContext() &&
      mutationRequestTracker.isLatest(localState, operationRequest),
    finish: () => {
      const completion = mutationRequestTracker.finish(
        localState,
        operationRequest
      );
      return (
        completion.isCurrentTracker &&
        completion.activeCount === 0 &&
        isCurrentContext()
      );
    },
  };
};

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchError: false,
  },
};

export const getters = {
  getAppointmentServices(_state) {
    return _state.records;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getAppointmentServiceById: _state => id => {
    return _state.records.find(record => record.id === Number(id)) || {};
  },
};

export const actions = {
  get: async function getAppointmentServices(
    { commit, state: localState = state, rootGetters = {} },
    { accountId, generation } = {}
  ) {
    const requestedAccountId = accountId ?? rootGetters.getCurrentAccountId;
    const initialContext = mutationContextGuard.begin(localState, {
      accountId: requestedAccountId,
      generation,
    });
    if (
      !mutationContextGuard.isCurrent(localState, initialContext, rootGetters)
    ) {
      return [];
    }

    const request = fetchRequestGuard.begin(localState, {
      accountId: requestedAccountId,
      generation,
    });
    const abortableRequest = readRequestTracker.begin(localState, 'list');
    const isCurrentRequest = () =>
      fetchRequestGuard.isCurrent(localState, request, rootGetters) &&
      readRequestTracker.isCurrent(localState, abortableRequest);
    if (!isCurrentRequest()) {
      readRequestTracker.finish(localState, abortableRequest);
      return [];
    }

    commit(types.SET_APPOINTMENT_SERVICE_UI_FLAG, {
      isFetching: true,
      isFetchError: false,
    });
    try {
      const response = await AppointmentServicesAPI.get(request.accountId, {
        signal: abortableRequest.signal,
      });
      const records = extractList(response);
      if (!isCurrentRequest()) return [];

      commit(types.SET_APPOINTMENT_SERVICES, records);
      return records;
    } catch {
      if (isCurrentRequest()) {
        commit(types.SET_APPOINTMENT_SERVICE_UI_FLAG, { isFetchError: true });
      }
      return [];
    } finally {
      if (isCurrentRequest()) {
        commit(types.SET_APPOINTMENT_SERVICE_UI_FLAG, { isFetching: false });
      }
      readRequestTracker.finish(localState, abortableRequest);
    }
  },

  // Audit fase-29 P0 — try/finally (sem catch) para preservar `error.response`
  // intacto até o caller, como já feito em appointmentsModule.js (incidente
  // 2026-05-07). O `throw new Error(error.response.data.message)` anterior
  // destruía `error.response` (o backend devolve `{errors: {...}}`, não
  // `message`), virando `Error("undefined")` e mostrando erro genérico/vazio.
  create: async function createAppointmentService(
    { commit, state: localState = state, rootGetters = {} },
    serviceData = {}
  ) {
    const { accountId, generation, ...apiData } = serviceData;
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
      group: 'isCreating',
    });
    if (!request.shouldStart) return undefined;

    commit(types.SET_APPOINTMENT_SERVICE_UI_FLAG, { isCreating: true });
    try {
      const response = await AppointmentServicesAPI.create(
        apiData,
        request.accountId
      );
      const record = extractRecord(response);
      if (!request.isCurrent()) return undefined;

      commit(types.ADD_APPOINTMENT_SERVICE, record);
      return record;
    } catch (error) {
      if (request.isCurrent()) throw error;
      return undefined;
    } finally {
      if (request.finish()) {
        commit(types.SET_APPOINTMENT_SERVICE_UI_FLAG, { isCreating: false });
      }
    }
  },

  update: async function updateAppointmentService(
    { commit, state: localState = state, rootGetters = {} },
    { id, accountId, generation, ...updateData } = {}
  ) {
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
      group: 'isUpdating',
      identity: id,
    });
    if (!request.shouldStart) return undefined;

    commit(types.SET_APPOINTMENT_SERVICE_UI_FLAG, { isUpdating: true });
    try {
      const response = await AppointmentServicesAPI.update(
        id,
        updateData,
        request.accountId
      );
      const record = extractRecord(response);
      if (!request.isCurrent()) return undefined;

      commit(types.EDIT_APPOINTMENT_SERVICE, record);
      return record;
    } catch (error) {
      if (request.isCurrent()) throw error;
      return undefined;
    } finally {
      if (request.finish()) {
        commit(types.SET_APPOINTMENT_SERVICE_UI_FLAG, { isUpdating: false });
      }
    }
  },

  delete: async function deleteAppointmentService(
    { commit, state: localState = state, rootGetters = {} },
    payload
  ) {
    const { id, accountId, generation } = normalizeMemberPayload(payload);
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
      group: 'isDeleting',
      identity: id,
    });
    if (!request.shouldStart) return undefined;

    commit(types.SET_APPOINTMENT_SERVICE_UI_FLAG, { isDeleting: true });
    try {
      await AppointmentServicesAPI.delete(id, request.accountId);
      if (!request.isCurrent()) return undefined;

      commit(types.DELETE_APPOINTMENT_SERVICE, id);
      return true;
    } catch (error) {
      if (request.isCurrent()) throw error;
      return undefined;
    } finally {
      if (request.finish()) {
        commit(types.SET_APPOINTMENT_SERVICE_UI_FLAG, { isDeleting: false });
      }
    }
  },
};

export const mutations = {
  RESET_ACCOUNT_DATA(_state) {
    invalidateAccountRequestContext(_state);
    readRequestTracker.reset(_state);
    mutationRequestTracker.reset(_state);
    _state.records = [];
    _state.uiFlags = {
      isFetching: false,
      isFetchingItem: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      isFetchError: false,
    };
  },

  [types.SET_APPOINTMENT_SERVICE_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_APPOINTMENT_SERVICES]: MutationHelpers.set,
  [types.ADD_APPOINTMENT_SERVICE]: MutationHelpers.create,
  [types.EDIT_APPOINTMENT_SERVICE]: MutationHelpers.update,
  [types.DELETE_APPOINTMENT_SERVICE]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
